import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { computeScaleLayout, type ScaleLayoutInput } from './band-scale-layout';
import styles from './governance.module.css';

/**
 * The band scale, drawn: one horizontal bar from minScore to maxScore, bands
 * as coloured segments carrying their code and grade points, the pass
 * threshold as a marked vertical line, and every gap or overlap rendered in a
 * diagonal error hatch — so an invalid configuration is VISIBLE before the
 * validator (client or server) says a word.
 *
 * Geometry comes from computeScaleLayout, which shares the validator's
 * step-unit arithmetic; this component only maps unit-interval coordinates to
 * a 1000-unit viewBox. Theme-awareness is by inheritance: axis ink is
 * currentColor from the module CSS, structural colours are CSS variables, and
 * only the band palette is fixed (bands are ordinal positions on a ruler, not
 * theme surfaces). The whole drawing is one labelled image for assistive
 * technology — the same information is available as text in the band table
 * and the live validation report beneath it, so the SVG summarises rather
 * than being the only carrier.
 */

const VIEW_W = 1000;
const BAR_Y = 26;
const BAR_H = 44;
const VIEW_H = 108;
const PALETTE_SIZE = 6;

export function BandScale({ input, title }: { input: ScaleLayoutInput; title: string }) {
  const { t } = useTranslation();
  const layout = useMemo(() => computeScaleLayout(input), [input]);

  if (layout.degenerate) {
    return (
      <p className={styles.scaleDegenerate} role="status">
        {t('grading.scale.degenerate')}
      </p>
    );
  }

  const x = (fraction: number) => fraction * VIEW_W;
  const errorSegments = layout.segments.filter((s) => s.kind !== 'band');

  return (
    <figure className={styles.scaleFigure}>
      <svg
        className={styles.scaleSvg}
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        role="img"
        aria-label={title}
        preserveAspectRatio="none"
      >
        <title>{title}</title>
        <defs>
          {/* The error hatch. patternTransform keeps the stripes diagonal at
              any rendered width; the stroke colour follows the theme via the
              CSS class on the pattern's line. */}
          <pattern
            id="band-scale-error-hatch"
            width="8"
            height="8"
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(45)"
          >
            <line x1="0" y1="0" x2="0" y2="8" className={styles.scaleHatchStroke} />
          </pattern>
        </defs>

        {/* Band segments first, then error segments ON TOP: an overlap is a
            region two bands both claim, and the hatch must win the paint
            order or the mistake hides under whichever band drew last. */}
        {layout.segments
          .filter((segment) => segment.kind === 'band')
          .map((segment) => {
            const width = Math.max(0, x(segment.x1) - x(segment.x0));
            const label = `${segment.code ?? ''}${
              segment.gradePoints !== null && segment.gradePoints !== undefined
                ? ` · ${segment.gradePoints}`
                : ''
            }`;
            return (
              <g key={`band-${segment.code}-${segment.from}`}>
                <rect
                  className={`${styles.scaleBand} ${
                    styles[`scaleBandFill${(segment.colourIndex ?? 0) % PALETTE_SIZE}`]
                  }`}
                  x={x(segment.x0)}
                  y={BAR_Y}
                  width={width}
                  height={BAR_H}
                >
                  <title>
                    {t('grading.scale.bandTitle', {
                      code: segment.code,
                      label: segment.label,
                      from: segment.from,
                      to: segment.to,
                    })}
                  </title>
                </rect>
                {/* Labels only where they fit; a 12-unit sliver with a code
                    stamped over its neighbours is worse than no label. */}
                {width > 36 && (
                  <text
                    className={styles.scaleBandLabel}
                    x={x(segment.x0) + width / 2}
                    y={BAR_Y + BAR_H / 2 + 4}
                    textAnchor="middle"
                  >
                    {label}
                  </text>
                )}
              </g>
            );
          })}

        {errorSegments.map((segment, index) => (
          <rect
            key={`error-${index}`}
            className={styles.scaleErrorFill}
            x={x(segment.x0)}
            y={BAR_Y}
            width={Math.max(2, x(segment.x1) - x(segment.x0))}
            height={BAR_H}
          >
            <title>
              {segment.kind === 'gap'
                ? t('grading.scale.gapTitle', {
                    from: segment.from,
                    to: segment.to,
                    first: segment.between?.[0],
                    second: segment.between?.[1],
                  })
                : t('grading.scale.overlapTitle', {
                    from: segment.from,
                    to: segment.to,
                    first: segment.between?.[0],
                    second: segment.between?.[1],
                  })}
            </title>
          </rect>
        ))}

        {/* Ticks under the bar: the range ends and each band boundary. */}
        {layout.ticks.map((tick) => (
          <g key={`tick-${tick.value}`}>
            <line
              className={styles.scaleTick}
              x1={x(tick.x)}
              y1={BAR_Y + BAR_H}
              x2={x(tick.x)}
              y2={BAR_Y + BAR_H + 6}
            />
            <text
              className={styles.scaleTickLabel}
              x={x(tick.x)}
              y={BAR_Y + BAR_H + 18}
              textAnchor={tick.x === 0 ? 'start' : tick.x === 1 ? 'end' : 'middle'}
            >
              {tick.value}
            </text>
          </g>
        ))}

        {/* The pass threshold: a full-height line with a flag above the bar.
            Drawn even when out of range (clamped to an edge) because a
            threshold of 25 on a 0–20 scale is exactly the mistake the
            drawing exists to make conspicuous. */}
        <line
          className={styles.scaleThreshold}
          x1={x(layout.threshold.x)}
          y1={8}
          x2={x(layout.threshold.x)}
          y2={BAR_Y + BAR_H + 6}
        />
        <polygon
          className={styles.scaleThresholdMarker}
          points={`${x(layout.threshold.x) - 5},2 ${x(layout.threshold.x) + 5},2 ${x(layout.threshold.x)},10`}
        />
        <text
          className={styles.scaleThresholdLabel}
          x={x(layout.threshold.x)}
          y={VIEW_H - 4}
          textAnchor={layout.threshold.x < 0.06 ? 'start' : layout.threshold.x > 0.94 ? 'end' : 'middle'}
        >
          {t('grading.scale.thresholdLabel', { value: layout.threshold.value })}
        </text>
      </svg>

      <figcaption className={styles.scaleLegend}>
        <span>
          <span className={styles.scaleLegendSwatchThreshold} aria-hidden="true" />
          {t('grading.scale.legendThreshold')}
        </span>
        <span>
          <span className={styles.scaleLegendSwatchError} aria-hidden="true" />
          {t('grading.scale.legendError')}
        </span>
      </figcaption>
    </figure>
  );
}
